import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, RefreshCw } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import V2AuthButton from "./components/V2AuthButton";

export default function V2VerifyEmailSent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [maskedEmail, setMaskedEmail] = useState("");
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState("");

  // Check auth and get email
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    const userStr = localStorage.getItem("user");

    if (!authToken || !userStr) {
      navigate("/v2/auth/login", { replace: true });
      return;
    }

    try {
      const user = JSON.parse(userStr);
      setMaskedEmail(maskEmail(user.email));
    } catch (err) {
      console.error("Failed to parse user data:", err);
      navigate("/v2/auth/login", { replace: true });
    }
  }, [navigate]);

  // Poll for email verification (e.g. verified in another tab)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data: user } = await betaAPI.getCurrentUser();
        if (user?.is_verified) {
          clearInterval(interval);
          localStorage.setItem("user", JSON.stringify(user));
          const pendingInvite = sessionStorage.getItem("pendingInviteUrl");
          if (pendingInvite) {
            sessionStorage.removeItem("pendingInviteUrl");
            navigate(pendingInvite, { replace: true });
          } else {
            navigate("/v2", { replace: true });
          }
        }
      } catch (err) {
        // Silently ignore polling errors
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !canResend) {
      setCanResend(true);
    }
  }, [countdown, canResend]);

  const maskEmail = (email) => {
    if (!email) return "";
    const [username, domain] = email.split("@");
    if (username.length <= 5) {
      return `${username.charAt(0)}***@${domain}`;
    }
    const maskedUsername = username.slice(0, 3) + "***" + username.slice(-2);
    return `${maskedUsername}@${domain}`;
  };

  const handleResend = async () => {
    setIsResending(true);
    setError("");

    try {
      await betaAPI.resendVerificationEmail();
      showToast.success(t("auth.verifyEmailSent.sentToast"));
      setCanResend(false);
      setCountdown(60);
    } catch (err) {
      console.error("Resend error:", err);
      setError(t("auth.verifyEmailSent.resendFailed"));
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    navigate("/v2/auth/login", { replace: true });
  };

  return (
    <>
      {/* Icon */}
      <div className="mb-6 flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FEF3E7]">
          <Mail size={28} className="text-[#E07A3A]" />
        </div>
      </div>

      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.common.checkYourEmail")}
        </h1>
        <p className="text-sm text-[#6B7280]">
          <Trans
            i18nKey="auth.verifyEmailSent.sentTo"
            values={{ email: maskedEmail }}
            components={{
              email: <span className="font-medium text-[#374151]" />,
            }}
          />
        </p>
      </div>

      {/* Info box */}
      <div
        className="mb-6 rounded-xl p-4"
        style={{
          background:
            "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
          border: "1px solid rgba(232, 133, 74, 0.15)",
        }}
      >
        <p className="text-sm text-[#374151]">
          {t("auth.verifyEmailSent.info")}
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Resend button */}
      <V2AuthButton
        variant="secondary"
        onClick={handleResend}
        disabled={!canResend || isResending}
        loading={isResending}
      >
        <span className="flex items-center justify-center gap-2">
          <RefreshCw size={16} />
          {canResend
            ? t("auth.verifyEmailSent.resend")
            : t("auth.verifyEmailSent.resendIn", { seconds: countdown })}
        </span>
      </V2AuthButton>

      {/* Back to login */}
      <div className="mt-6 text-center">
        <p className="text-sm text-[#6B7280]">
          {t("auth.verifyEmailSent.wrongEmail")}{" "}
          <button
            onClick={handleLogout}
            className="font-medium text-[#E07A3A] transition-colors hover:text-[#D66B2E]"
          >
            {t("auth.common.backToLogin")}
          </button>
        </p>
      </div>
    </>
  );
}
